import styles from './CloudinaryImage.module.scss'
import mediaFileObject from '@/entities/mediaFile/interface'
import cloudinaryUrlGenerator from '@/integrations/cloudinary/cloudinaryUrlGenerator'
import { AdvancedImage, AdvancedVideo, lazyload, placeholder, responsive } from '@cloudinary/react'
import { Plugins } from '@cloudinary/html'
import { fill, scale } from '@cloudinary/url-gen/actions/resize'
import { Cloudinary, CloudinaryVideo } from '@cloudinary/url-gen'
import { useDevicePixelRatio } from 'use-device-pixel-ratio'
import { dpr } from '@cloudinary/url-gen/actions/delivery'
import { useContext } from 'react'
import { PrintConfigContext } from '@/app/providers/PrintConfigProvider'

export interface CloudinaryImageProps {
  image: mediaFileObject
  lazyload?: boolean | 'placeholder'
  responsive?: boolean | number[]
  placeholder?: boolean
  width?: number
  height?: number
  sizeAttributes?: boolean
  dpr?: boolean | number
  cropType?: 'fill' | 'scale'
  aspectRatio?: number
  titleDefault?: string
  altDefault?: string
  className?: string
  style?: any
  placeholderClassName?: string
  cld?: Cloudinary
  trackingStringClick?: string
  trackingStringDblClick?: string
}
const CloudinaryImage = ({
  image,
  lazyload: lazyloadOption = true,
  responsive: responsiveOption = true,
  placeholder: placeholderOption = false,
  width,
  height,
  sizeAttributes = false,
  dpr: dprOption = true,
  cropType = 'fill',
  aspectRatio,
  titleDefault,
  altDefault,
  className = '',
  style,
  placeholderClassName = '',
  cld,
  trackingStringClick,
  trackingStringDblClick,
}: CloudinaryImageProps) => {
  const deviceDpr = useDevicePixelRatio()
  const printConfig = useContext(PrintConfigContext)

  lazyloadOption = printConfig.images.lazyloadEnabled === false ? false : lazyloadOption

  if (!image?.publicId) {
    return <div className={`${styles.placeholder} ${placeholderClassName}`} />
  }
  if (!cld) {
    cld = cloudinaryUrlGenerator
  }
  const mediaFile = image.resourceType === 'video' ? cld.video(image.publicId) : cld.image(image.publicId)

  let options: { [x: string]: any } = {}
  if (aspectRatio) {
    options.aspect_ratio = aspectRatio
  }

  if (image.resourceType !== 'video') {
    mediaFile.format('auto').quality('auto')
  } else {
    mediaFile.format('auto').quality(70)
  }

  if (dprOption && image.resourceType !== 'video') {
    const imageDpr = Number.isInteger(dprOption) ? dprOption : deviceDpr
    mediaFile.delivery(dpr(imageDpr + '.0'))
  } else {
    // omit dpr for videos
    //mediaFile.delivery(dpr('1.0'))
  }

  let cropSettings
  switch (cropType) {
    case 'fill':
      cropSettings = fill()
      break
    case 'scale':
      cropSettings = scale()
      break
  }
  if (!responsiveOption) {
    if (width) {
      cropSettings.width(width)
    }
    if (height) {
      cropSettings.height(height)
    }
  }
  if (aspectRatio) {
    cropSettings.aspectRatio(aspectRatio)
  }
  if (cropType === 'fill' && (aspectRatio || width || height)) {
    if (mediaFile instanceof CloudinaryVideo) {
      // Videos cannot handle gravity('auto') on mobile
      // @ts-ignore
      cropSettings.gravity('center')
    } else {
      // @ts-ignore
      cropSettings.gravity('auto')
    }
  }
  mediaFile.resize(cropSettings)

  // see: https://cloudinary.com/documentation/react_image_transformations#plugins
  let plugins: Plugins = []

  // placeholder
  // Note: Not using recommended plugin order, as this loads the display image before placeholder image | see: https://cloudinary.com/documentation/react_image_transformations#plugin_order
  if (placeholderOption || lazyloadOption === 'placeholder') {
    plugins.push(placeholder({ mode: 'blur' })) // see: https://cloudinary.com/documentation/react_image_transformations#image_placeholders
  }

  // lazyload
  if (lazyloadOption) {
    plugins.push(lazyload())
  }

  // responsive breakpoints
  if (responsiveOption) {
    plugins.push(responsive({ steps: 200 }))
  }

  const getSizeAttributes = () => {
    if (sizeAttributes) {
      // custom sizes
      if (width && height) {
        return { width: width, height: height }
      }

      // dynamic sizes by aspect ratio
      if (aspectRatio) {
        if (width) {
          return { width: width, height: width / aspectRatio }
        }
        if (height) {
          return { width: height * aspectRatio, height: height }
        }
      }

      // intrinsic sizes
      return { width: image.width, height: image.height }
    }
    return {}
  }

  const getTrackingAttributes = () => {
    let trackingAttributes: Record<string, string> = {}
    // if "trackingStringClick" is set, build "data-tr-click={trackingStringClick}"
    if (trackingStringClick) {
      trackingAttributes['data-tr-click'] = trackingStringClick
    }

    // if "trackingStringDblClick" is set, build "data-tr-click={trackingStringDblClick}"
    if (trackingStringDblClick) {
      trackingAttributes['data-tr-dblclick'] = trackingStringDblClick
    }
    return trackingAttributes
  }

  return (() => {
    if (mediaFile instanceof CloudinaryVideo) {
      const posterImage = mediaFile.addTransformation('so_0').toURL()
      return <AdvancedVideo cldVid={mediaFile} poster={posterImage} plugins={plugins} autoPlay playsInline loop muted />
    }
    return (
      <AdvancedImage
        className={className}
        cldImg={mediaFile}
        plugins={plugins}
        title={image.title ?? titleDefault ?? ''}
        alt={image.description ?? altDefault ?? ''}
        loading={lazyloadOption === true ? 'lazy' : 'auto'}
        style={style ?? {}}
        {...getSizeAttributes()}
        {...getTrackingAttributes()}
      />
    )
  })()
}

export default CloudinaryImage
